import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  type GeneratedAuthSessionResponse,
  type GeneratedCurrentUserResponse,
  type GeneratedLoginRequest,
  type GeneratedLoginResponse,
} from '../../lib/api/generated-auth';
import type {
  GeneratedCreateBusinessResponse,
  GeneratedGetBusinessResponse,
  GeneratedUpdateBusinessResponse,
} from '../../lib/api/generated-business';
import { currentUserRequest, loginRequest } from '../../lib/api/auth-client';
import { registerUnauthorizedHandler } from '../../lib/api/unauthorized-handler';
import {
  clearSession,
  getStoredSession,
  type BusinessReference,
  normalizeBusinessReference,
  normalizeBusinessSnapshot,
  persistSession,
  type AuthSession,
} from './session';
import { LOGIN_PATH } from './auth-routing';

interface LoginInput {
  credentials: GeneratedLoginRequest;
  email: string;
  rememberMe: boolean;
  selectedBusinessId?: number | null;
}

interface PrepareLoginInput {
  credentials: GeneratedLoginRequest;
  email: string;
  rememberMe: boolean;
}

export interface PreparedLoginAttempt {
  response: GeneratedLoginResponse;
  email: string;
  rememberMe: boolean;
  businesses: BusinessReference[];
  selectedBusinessId: number | null;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isHydrating: boolean;
  session: AuthSession | null;
  acceptAuthSession: (input: AcceptAuthSessionInput) => Promise<AuthSession | null>;
  prepareLogin: (input: PrepareLoginInput) => Promise<PreparedLoginAttempt>;
  completeLogin: (
    attempt: PreparedLoginAttempt,
    selectedBusinessId?: number | null
  ) => Promise<AuthSession>;
  login: (input: LoginInput) => Promise<AuthSession>;
  syncAccessibleBusinesses: (businesses: BusinessReference[]) => void;
  switchBusiness: (business: BusinessReference) => void;
  syncCurrentUser: (user: GeneratedCurrentUserResponse) => void;
  syncBusinessSnapshot: (
    business:
      | GeneratedCreateBusinessResponse
      | GeneratedGetBusinessResponse
      | GeneratedUpdateBusinessResponse
  ) => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

interface AcceptAuthSessionInput {
  response: GeneratedAuthSessionResponse;
  email?: string;
  rememberMe: boolean;
  defaultHasBusiness?: boolean;
  selectedBusinessId?: number | null;
  fallbackSession?: AuthSession | null;
}

function getString(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function getBoolean(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === 'boolean' ? value : null;
}

function getUserType(record: Record<string, unknown>, key: string) {
  const value = record[key];

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === 'owner') {
    return 'Owner';
  }

  if (normalized === 'staff') {
    return 'Staff';
  }

  return null;
}

function getFlexibleNumber(record: Record<string, unknown>, key: string) {
  const value = record[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getNestedRecord(value: unknown) {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function getDisplayName(record: Record<string, unknown> | null) {
  const firstName = getString(record ?? {}, 'first_name');
  const lastName = getString(record ?? {}, 'last_name');
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return fullName || getString(record ?? {}, 'name');
}

function dedupeBusinesses(businesses: BusinessReference[]) {
  const seenIds = new Set<number>();

  return businesses.filter((business) => {
    if (seenIds.has(business.id)) {
      return false;
    }

    seenIds.add(business.id);
    return true;
  });
}

function normalizeAccessibleBusinesses(
  payload: Record<string, unknown>,
  nestedData: Record<string, unknown> | null,
  businessReference: BusinessReference | null,
  businessSnapshot: ReturnType<typeof normalizeBusinessSnapshot>
) {
  const nestedBusinesses =
    nestedData && Array.isArray(nestedData.businesses)
      ? nestedData.businesses
      : [];
  const rawBusinesses = [
    ...nestedBusinesses,
    ...(Array.isArray(payload.businesses) ? payload.businesses : []),
  ];
  const normalizedBusinesses = rawBusinesses
    .map((business) => normalizeBusinessReference(business))
    .filter((business): business is BusinessReference => business !== null);

  return dedupeBusinesses(
    [
      ...(businessSnapshot ? [businessSnapshot] : []),
      ...(businessReference ? [businessReference] : []),
      ...normalizedBusinesses,
    ].filter((business): business is BusinessReference => business !== null)
  );
}

function resolveSelectedBusinessId(
  payload: Record<string, unknown>,
  nestedData: Record<string, unknown> | null,
  businesses: BusinessReference[],
  explicitSelectedBusinessId?: number | null
) {
  const availableIds = new Set(businesses.map((business) => business.id));
  const selectedCandidateIds = [
    explicitSelectedBusinessId ?? null,
    getFlexibleNumber(payload, 'selected_business_id'),
    getFlexibleNumber(payload, 'selectedBusinessId'),
    getFlexibleNumber(nestedData ?? {}, 'selected_business_id'),
    getFlexibleNumber(nestedData ?? {}, 'selectedBusinessId'),
  ];

  for (const candidateId of selectedCandidateIds) {
    if (candidateId !== null && availableIds.has(candidateId)) {
      return candidateId;
    }
  }

  return businesses[0]?.id ?? null;
}

function applyBusinessRole<T extends BusinessReference | NonNullable<ReturnType<typeof normalizeBusinessSnapshot>>>(
  business: T | null,
  role: BusinessReference['role'] | undefined
): T | null {
  if (!business || !role || business.role === role) {
    return business;
  }

  return {
    ...business,
    role,
  };
}

function normalizeSession(
  response: GeneratedAuthSessionResponse,
  email: string,
  rememberMe: boolean,
  defaultHasBusiness = false,
  selectedBusinessId?: number | null
): AuthSession {
  const payload = getNestedRecord(response) ?? {};
  const nestedData = getNestedRecord(payload.data);
  const nestedUser =
    getNestedRecord(payload.user) ?? getNestedRecord(nestedData?.user ?? null);
  const businessReference =
    normalizeBusinessReference(payload.business) ??
    normalizeBusinessReference(nestedData?.business ?? null);
  const businessSnapshot =
    normalizeBusinessSnapshot(payload.business) ??
    normalizeBusinessSnapshot(nestedData?.business ?? null);
  const accessibleBusinesses = normalizeAccessibleBusinesses(
    payload,
    nestedData,
    businessReference,
    businessSnapshot
  );
  const resolvedSelectedBusinessId = resolveSelectedBusinessId(
    payload,
    nestedData,
    accessibleBusinesses,
    selectedBusinessId
  );
  const selectedBusinessSnapshot =
    businessSnapshot?.id === resolvedSelectedBusinessId ? businessSnapshot : null;
  const selectedBusinessReference =
    selectedBusinessSnapshot ??
    (businessReference?.id === resolvedSelectedBusinessId
      ? businessReference
      : null) ??
    accessibleBusinesses.find((business) => business.id === resolvedSelectedBusinessId) ??
    null;

  return {
    token:
      getString(payload, 'accessToken') ??
      getString(payload, 'token') ??
      getString(payload, 'access_token') ??
      getString(payload, 'jwt') ??
      getString(nestedData ?? {}, 'accessToken') ??
      getString(nestedData ?? {}, 'token'),
    userId: getFlexibleNumber(nestedUser ?? {}, 'id') ?? undefined,
    userType: getUserType(nestedUser ?? {}, 'user_type') ?? undefined,
    email: getString(nestedUser ?? {}, 'email') ?? email,
    name: getDisplayName(nestedUser),
    rememberMe,
    hasBusiness:
      getBoolean(payload, 'has_business') ??
      getBoolean(payload, 'hasBusiness') ??
      getBoolean(nestedData ?? {}, 'has_business') ??
      getBoolean(nestedData ?? {}, 'hasBusiness') ??
      (selectedBusinessReference ? true : null) ??
      (selectedBusinessSnapshot ? true : null) ??
      (accessibleBusinesses.length > 0 ? true : null) ??
      defaultHasBusiness,
    accessibleBusinesses:
      accessibleBusinesses.length > 0 ? accessibleBusinesses : undefined,
    businessReference: selectedBusinessReference ?? undefined,
    businessSnapshot: selectedBusinessSnapshot ?? undefined,
  };
}

function prepareLoginAttempt(
  response: GeneratedLoginResponse,
  email: string,
  rememberMe: boolean
): PreparedLoginAttempt {
  const payload = getNestedRecord(response) ?? {};
  const nestedData = getNestedRecord(payload.data);
  const businessReference =
    normalizeBusinessReference(payload.business) ??
    normalizeBusinessReference(nestedData?.business ?? null);
  const businessSnapshot =
    normalizeBusinessSnapshot(payload.business) ??
    normalizeBusinessSnapshot(nestedData?.business ?? null);
  const businesses = normalizeAccessibleBusinesses(
    payload,
    nestedData,
    businessReference,
    businessSnapshot
  );

  return {
    response,
    email,
    rememberMe,
    businesses,
    selectedBusinessId: resolveSelectedBusinessId(
      payload,
      nestedData,
      businesses
    ),
  };
}

async function enrichBusinessSessionUser(
  session: AuthSession
): Promise<AuthSession> {
  if (!session.token || !session.hasBusiness || session.userType) {
    return session;
  }

  try {
    const user = await currentUserRequest(session.token);

    return {
      ...session,
      userId: typeof user.id === 'number' ? user.id : session.userId,
      userType:
        getUserType(user as unknown as Record<string, unknown>, 'user_type') ??
        session.userType,
      email: user.email || session.email,
      name:
        getDisplayName(user as unknown as Record<string, unknown>) ??
        session.name,
    };
  } catch (error) {
    return session;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [session, setSession] = React.useState<AuthSession | null>(null);
  const [isHydrating, setIsHydrating] = React.useState(true);
  const sessionRef = React.useRef(session);
  const isExpiringRef = React.useRef(false);

  React.useEffect(() => {
    setSession(getStoredSession());
    setIsHydrating(false);
  }, []);

  React.useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Allow re-triggering the expiry flow once the user signs back in.
  React.useEffect(() => {
    if (session?.token) {
      isExpiringRef.current = false;
    }
  }, [session?.token]);

  // Auto-logout when any authenticated request returns 401 (expired/invalid token).
  React.useEffect(
    () =>
      registerUnauthorizedHandler(() => {
        // Ignore 401s with no active session (e.g. wrong-password on the login form),
        // and dedupe the burst of parallel 401s a screen can fire at once.
        if (!sessionRef.current?.token || isExpiringRef.current) {
          return;
        }

        isExpiringRef.current = true;
        clearSession();
        setSession(null);
        navigate(LOGIN_PATH, {
          replace: true,
          state: { notice: 'A sua sessão expirou. Inicie sessão novamente.' },
        });
      }),
    [navigate]
  );

  const prepareLogin = React.useCallback(
    async ({ credentials, email, rememberMe }: PrepareLoginInput) => {
      const response = await loginRequest(credentials);

      return prepareLoginAttempt(response, email, rememberMe);
    },
    []
  );

  const acceptAuthSession = React.useCallback(
    async ({
      response,
      email = '',
      rememberMe,
      defaultHasBusiness = false,
      selectedBusinessId,
      fallbackSession = null,
    }: AcceptAuthSessionInput) => {
      const normalizedSession = normalizeSession(
        response,
        email,
        rememberMe,
        defaultHasBusiness,
        selectedBusinessId
      );
      const mergedSession = {
        ...normalizedSession,
        userId: normalizedSession.userId ?? fallbackSession?.userId,
        userType: normalizedSession.userType ?? fallbackSession?.userType,
        email: normalizedSession.email || fallbackSession?.email || '',
        name: normalizedSession.name ?? fallbackSession?.name ?? null,
        accessibleBusinesses:
          normalizedSession.accessibleBusinesses ??
          fallbackSession?.accessibleBusinesses,
        businessReference:
          normalizedSession.businessReference ?? fallbackSession?.businessReference,
        businessSnapshot:
          normalizedSession.businessSnapshot ?? fallbackSession?.businessSnapshot,
      } satisfies AuthSession;
      const nextSession = await enrichBusinessSessionUser(mergedSession);

      if (!nextSession.token) {
        return null;
      }

      persistSession(nextSession);
      setSession(nextSession);
      return nextSession;
    },
    []
  );

  const completeLogin = React.useCallback(
    async (
      attempt: PreparedLoginAttempt,
      selectedBusinessId?: number | null
    ) => {
      const nextSession = await acceptAuthSession({
        response: attempt.response,
        email: attempt.email,
        rememberMe: attempt.rememberMe,
        defaultHasBusiness: false,
        selectedBusinessId:
          selectedBusinessId ?? attempt.selectedBusinessId,
      });

      if (!nextSession) {
        throw new Error('Não foi possível concluir a autenticação agora.');
      }

      return nextSession;
    },
    [acceptAuthSession]
  );

  const login = React.useCallback(async ({ credentials, email, rememberMe, selectedBusinessId }: LoginInput) => {
    const attempt = await prepareLogin({ credentials, email, rememberMe });

    return completeLogin(
      attempt,
      selectedBusinessId ?? attempt.selectedBusinessId
    );
  }, [completeLogin, prepareLogin]);

  const syncAccessibleBusinesses = React.useCallback((businesses: BusinessReference[]) => {
    setSession((currentSession) => {
      if (!currentSession) {
        return currentSession;
      }

      const nextAccessibleBusinesses = dedupeBusinesses(businesses);
      const activeBusinessId =
        currentSession.businessSnapshot?.id ?? currentSession.businessReference?.id ?? null;
      const activeBusinessFromList =
        activeBusinessId === null
          ? null
          : nextAccessibleBusinesses.find((business) => business.id === activeBusinessId) ??
            null;
      const nextBusinessRole =
        activeBusinessFromList?.role ??
        currentSession.businessSnapshot?.role ??
        currentSession.businessReference?.role;
      const nextSession = {
        ...currentSession,
        accessibleBusinesses:
          nextAccessibleBusinesses.length > 0 ? nextAccessibleBusinesses : undefined,
        businessReference: applyBusinessRole(
          currentSession.businessReference ?? null,
          nextBusinessRole
        ) ?? undefined,
        businessSnapshot: applyBusinessRole(
          currentSession.businessSnapshot ?? null,
          nextBusinessRole
        ) ?? undefined,
      };

      persistSession(nextSession);
      return nextSession;
    });
  }, []);

  const switchBusiness = React.useCallback((business: BusinessReference) => {
    setSession((currentSession) => {
      if (!currentSession) {
        return currentSession;
      }

      const knownBusiness =
        currentSession.accessibleBusinesses?.find((item) => item.id === business.id) ?? null;
      const nextBusinessReference = {
        ...(knownBusiness ?? {}),
        ...business,
        role: business.role ?? knownBusiness?.role,
      } satisfies BusinessReference;
      const nextAccessibleBusinesses = dedupeBusinesses([
        ...(currentSession.accessibleBusinesses ?? []),
        nextBusinessReference,
      ]);
      const currentBusinessId =
        currentSession.businessSnapshot?.id ?? currentSession.businessReference?.id ?? null;
      const nextSession = {
        ...currentSession,
        hasBusiness: true,
        accessibleBusinesses:
          nextAccessibleBusinesses.length > 0 ? nextAccessibleBusinesses : undefined,
        businessReference: nextBusinessReference,
        businessSnapshot:
          currentBusinessId === nextBusinessReference.id
            ? applyBusinessRole(
                currentSession.businessSnapshot ?? null,
                nextBusinessReference.role
              ) ?? undefined
            : undefined,
      };

      persistSession(nextSession);
      return nextSession;
    });
  }, []);

  const syncCurrentUser = React.useCallback((user: GeneratedCurrentUserResponse) => {
    setSession((currentSession) => {
      if (!currentSession) {
        return currentSession;
      }

      const nextSession = {
        ...currentSession,
        userId:
          typeof user.id === 'number'
            ? user.id
            : currentSession.userId,
        userType:
          getUserType(user as unknown as Record<string, unknown>, 'user_type') ??
          currentSession.userType,
        email: user.email || currentSession.email,
        name:
          getDisplayName(user as unknown as Record<string, unknown>) ??
          currentSession.name,
      };

      persistSession(nextSession);
      return nextSession;
    });
  }, []);

  const syncBusinessSnapshot = React.useCallback(
    (
      business:
        | GeneratedCreateBusinessResponse
        | GeneratedGetBusinessResponse
        | GeneratedUpdateBusinessResponse
    ) => {
      const businessReference = normalizeBusinessReference(business);
      const businessSnapshot = normalizeBusinessSnapshot(business);

      setSession((currentSession) => {
        if (!currentSession) {
          return currentSession;
        }

        const nextBusinessId =
          businessSnapshot?.id ??
          businessReference?.id ??
          currentSession.businessSnapshot?.id ??
          currentSession.businessReference?.id ??
          null;
        const matchingAccessibleBusiness =
          nextBusinessId === null
            ? null
            : currentSession.accessibleBusinesses?.find(
                (item) => item.id === nextBusinessId
              ) ?? null;
        const nextBusinessRole =
          matchingAccessibleBusiness?.role ??
          (currentSession.businessReference?.id === nextBusinessId
            ? currentSession.businessReference.role
            : undefined) ??
          (currentSession.businessSnapshot?.id === nextBusinessId
            ? currentSession.businessSnapshot.role
            : undefined);
        const nextBusinessReference =
          applyBusinessRole(
            businessSnapshot ?? businessReference ?? null,
            nextBusinessRole
          ) ?? currentSession.businessReference;
        const nextBusinessSnapshot =
          applyBusinessRole(
            businessSnapshot ?? null,
            nextBusinessRole
          ) ?? currentSession.businessSnapshot;
        const nextSession = {
          ...currentSession,
          hasBusiness: true,
          businessReference: nextBusinessReference,
          businessSnapshot: nextBusinessSnapshot,
        };

        persistSession(nextSession);
        return nextSession;
      });
    },
    []
  );

  const logout = React.useCallback(() => {
    clearSession();
    setSession(null);
  }, []);

  const value = React.useMemo(
    () => ({
      isAuthenticated: Boolean(session?.token),
      isHydrating,
      session,
      acceptAuthSession,
      prepareLogin,
      completeLogin,
      login,
      syncAccessibleBusinesses,
      switchBusiness,
      syncCurrentUser,
      syncBusinessSnapshot,
      logout,
    }),
    [
      acceptAuthSession,
      completeLogin,
      isHydrating,
      login,
      logout,
      prepareLogin,
      session,
      switchBusiness,
      syncAccessibleBusinesses,
      syncBusinessSnapshot,
      syncCurrentUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return context;
}
